import type React from "react";
import { useRef } from "react";

import { Search } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { DotsLoader, IconButton, Popover, PopoverTrigger } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Box, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import {
    StyledAutocompleteSearch,
    StyledAutocompleteTrigger,
} from "app/pages/sales-intelligence/pages/ai-agent/containers/CompanyAutocomplete/styles";
import { useRecipients } from "app/pages/sales-intelligence/pages/ai-agent/containers/Recipients/useRecipients";

import { RecipientsResults } from "./RecipientsResults";
import { StyledPopoverContent, StyledRecipientsTriggerText } from "./styles";

type Props = {
    value: Contact | null;
    onChange: (value: Contact | null) => void;
    domain: string;
    isDisabled?: boolean;
};

export const Recipients: React.FC<Props> = ({ value, onChange, domain, isDisabled }) => {
    const triggerButtonRef = useRef<HTMLDivElement | null>(null);
    const inputWrapperRef = useRef<HTMLDivElement | null>(null);

    const {
        isOpen,
        inputValue,
        search,
        data,
        isLoading,
        onSearchChange,
        setInputValue,
        onSelectContact,
        onClear,
        setIsOpen,
    } = useRecipients({ value, onChange, domain });

    const handleToggle = (_isOpen: boolean, event?: Event) => {
        if (inputWrapperRef.current?.contains(event?.target as Node)) {
            return;
        }

        if (isOpen && search) {
            setInputValue("");
        }
        setIsOpen((prev) => !prev);
    };

    const isSearchFieldVisible = isOpen || !value;

    const handleTriggerClick = () => {
        if (isDisabled) {
            return;
        }
        setIsOpen((prev) => !prev);
    };

    const handleSearchClick = () => {
        if (!data || isDisabled) {
            return;
        }
        setIsOpen(true);
    };

    return (
        <Box maxHeight="40px">
            <StyledAutocompleteTrigger
                ref={triggerButtonRef}
                onClick={handleTriggerClick}
                isVisible={!isSearchFieldVisible}
                isDisabled={isDisabled}
            >
                <StyledRecipientsTriggerText>
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.carbon[400]}
                    >
                        {value?.name || ""}
                    </Text>
                    <Text
                        fontVariant="listItem3"
                        color={colorsPalettes.carbon[300]}
                    >
                        {value?.title || ""}
                    </Text>
                </StyledRecipientsTriggerText>
                <Box margin="0 0 0 auto">
                    <IconButton
                        iconName="clear"
                        iconSize="xs"
                        type="flat"
                        onClick={onClear}
                    />
                </Box>
            </StyledAutocompleteTrigger>
            {isSearchFieldVisible ? (
                <StyledAutocompleteSearch
                    ref={inputWrapperRef}
                    onClick={handleSearchClick}
                >
                    <TextField
                        autoFocus={isOpen}
                        value={inputValue}
                        placeholder={translate("si.pages.ai_agent_email.recipient.placeholder")}
                        startAddon={<Search size="sm" />}
                        type="text"
                        onChange={isDisabled ? undefined : onSearchChange}
                        isDisabled={isDisabled}
                        endAddon={isLoading ? <DotsLoader /> : null}
                    />
                </StyledAutocompleteSearch>
            ) : null}
            <Popover
                isClickOutsideEnabled
                open={isOpen}
                onOpenChange={handleToggle}
                placement="bottom-start"
                initialFocus={-1}
            >
                <PopoverTrigger elementRef={triggerButtonRef} />
                <StyledPopoverContent>
                    <RecipientsResults
                        data={data}
                        search={search}
                        onSelectContact={onSelectContact}
                    />
                </StyledPopoverContent>
            </Popover>
        </Box>
    );
};
